import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Globe, Shield, Ship, AlertTriangle, TrendingUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SECTION_ICONS = [Globe, Shield, TrendingUp, AlertTriangle, Ship, TrendingUp];

export default function CountryPublicPage() {
  const { countryCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/seo/countries/${countryCode}/public`)
      .then(r => r.json())
      .then(d => { if (!d.error) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [countryCode]);

  if (loading) return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#0097A7]" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
      <p className="text-[#4E6974]">Country not found</p>
    </div>
  );

  const { name, code, region, hero_summary, at_a_glance, sections, related_countries } = data;
  const aag = at_a_glance || {};

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="country-public-page">
      <SEOHead
        title={`${name} Supply Chain Risk Profile | Nuvanta TradePath`}
        description={`Supply chain risk assessment for ${name}. Risk level, key exports, disruption events, and sourcing intelligence for Australian importers. Start a free trial.`}
      />

      {/* Nav */}
      <header>
        <nav className="bg-[#0A2533] border-b border-white/10" aria-label="Country page navigation">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-white font-bold text-lg">Nuvanta Solutions</span>
              <span className="text-[#4DD0E1] text-lg">|</span>
              <span className="text-[#4DD0E1] font-semibold text-lg">TradePath</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-white/80 hover:text-white text-sm transition-colors hidden sm:inline">Pricing</Link>
              <Link to="/insights" className="text-white/80 hover:text-white text-sm transition-colors hidden sm:inline">Insights</Link>
              <Link to="/signin" className="text-white/80 hover:text-white text-sm transition-colors">Sign in</Link>
              <Link to="/register" className="bg-[#00707C] hover:bg-[#005F6A] text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
                Start your trial
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content" className="max-w-5xl mx-auto px-6 py-8">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-[#4E6974] mb-6" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#0097A7] transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#4E6974]">Countries</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="font-medium text-[#0D3B4F]">{name}</span>
        </nav>

        {/* Hero */}
        <section className="bg-[#0A2533] rounded-xl p-8 sm:p-12 mb-8" data-testid="country-hero">
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-testid="country-h1">
            {name} Supply Chain Risk Profile
          </h1>
          <p className="text-[#4DD0E1] text-base sm:text-lg leading-relaxed mb-6 max-w-3xl">
            {hero_summary}
          </p>
          <Link to="/register">
            <Button className="bg-[#00707C] hover:bg-[#005F6A] text-white px-6 py-2.5 rounded-lg font-medium" data-testid="hero-cta">
              Start your free trial
            </Button>
          </Link>
        </section>

        {/* At a glance */}
        <section className="bg-white rounded-xl p-6 sm:p-8 mb-8 shadow-sm" data-testid="at-a-glance">
          <h2 className="text-lg font-bold text-[#0D3B4F] mb-4">At a glance</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#F5F5F5] rounded-lg p-4">
              <div className="text-xs text-[#4E6974] mb-1">Risk level</div>
              <div className="text-lg font-bold text-[#0D3B4F]">{aag.risk_label}</div>
            </div>
            <div className="bg-[#F5F5F5] rounded-lg p-4">
              <div className="text-xs text-[#4E6974] mb-1">Bilateral trade</div>
              <div className="text-lg font-bold text-[#0D3B4F]">{aag.trade_value}</div>
            </div>
            <div className="bg-[#F5F5F5] rounded-lg p-4 col-span-2 lg:col-span-1">
              <div className="text-xs text-[#4E6974] mb-1">Top exports to Australia</div>
              <div className="text-sm font-medium text-[#0D3B4F]">{aag.top_exports?.join(', ')}</div>
            </div>
            <div className="bg-[#F5F5F5] rounded-lg p-4 col-span-2 lg:col-span-1">
              <div className="text-xs text-[#4E6974] mb-1">Primary ports</div>
              <div className="text-sm font-medium text-[#0D3B4F]">{aag.primary_ports?.join(', ')}</div>
            </div>
          </div>
        </section>

        {/* Narrative sections */}
        {sections?.map((section, i) => {
          const Icon = SECTION_ICONS[i % SECTION_ICONS.length];
          return (
            <section key={i} className="bg-white rounded-xl p-6 sm:p-8 mb-6 shadow-sm" data-testid={`section-${i}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#0097A7]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-[#0097A7]" />
                </div>
                <h2 className="text-lg font-bold text-[#0D3B4F]">{section.title}</h2>
              </div>
              <div
                className="text-[#4E6974] leading-relaxed pl-11 prose-a:text-[#0097A7] prose-a:hover:underline"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
              />
            </section>
          );
        })}

        {/* Related countries */}
        {related_countries?.length > 0 && (
          <section className="bg-white rounded-xl p-6 sm:p-8 mb-6 shadow-sm" data-testid="related-countries">
            <h2 className="text-lg font-bold text-[#0D3B4F] mb-4">Related country profiles</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {related_countries.map(rc => (
                <Link key={rc.code} to={`/countries/${rc.code.toLowerCase()}`}
                  className="bg-[#F5F5F5] hover:bg-[#0097A7]/10 rounded-lg p-4 text-center transition-colors group">
                  <div className="text-sm font-medium text-[#0D3B4F] group-hover:text-[#0097A7]">{rc.name}</div>
                  <div className="text-xs text-[#4E6974] mt-1">Risk profile</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="bg-[#0D3B4F] rounded-xl p-8 sm:p-12 text-center" data-testid="final-cta">
          <h2 className="text-2xl font-bold text-white mb-3">
            Get the full {name} risk profile in TradePath
          </h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            Real-time monitoring, supplier-level alerts, and weekly briefings. From AUD 2,500 per month or AUD 25,000 per year upfront.
          </p>
          <Link to="/register">
            <Button className="bg-[#00707C] hover:bg-[#005F6A] text-white px-8 py-3 rounded-lg font-medium text-base" data-testid="final-cta-btn">
              Start your free trial
            </Button>
          </Link>
        </section>

        {/* Footer nav */}
        <nav className="flex flex-wrap gap-x-6 gap-y-2 justify-center mt-8 text-sm text-[#4E6974]" aria-label="Footer links">
          <Link to="/" className="hover:text-[#0097A7]">Home</Link>
          <Link to="/pricing" className="hover:text-[#0097A7]">Pricing</Link>
          <Link to="/insights" className="hover:text-[#0097A7]">Insights</Link>
          <Link to="/trust" className="hover:text-[#0097A7]">Trust</Link>
          <Link to="/register" className="hover:text-[#0097A7]">Start trial</Link>
        </nav>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-[#0A2533] mt-12">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[#4DD0E1] text-sm font-semibold mb-1">
            A <a href="https://nuvantasolutions.com.au" className="underline hover:text-white">Nuvanta Solutions</a> product
          </p>
          <p className="text-white/40 text-xs">&copy; 2026 Nuvanta Solutions Pty Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
